import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Boxes,
  ChevronDown,
  Gauge,
  LogOut,
  Target,
  Users,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useCan } from '../../hooks/useCan';
import { logout } from '../../lib/keycloak';

function initialsOf(name: string | undefined | null): string {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function UserMenu() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const canUsers = useCan('users', 'view');
  const canClusters = useCan('clusters', 'view');
  const canOps = useCan('ops', 'view');

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user?.name ?? 'Usuário';

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex h-7 items-center gap-2 rounded-md border border-border bg-surface pl-1 pr-2 text-[11.5px] font-medium text-text hover:bg-surfaceAlt focus:outline-none focus:ring-2 focus:ring-accent/40"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          aria-hidden
          className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white"
        >
          {initialsOf(user?.name)}
        </span>
        <span className="hidden max-w-[140px] truncate sm:block">{name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-textMute" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-md border border-border bg-surface shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="user-menu-activity"
            onClick={() => {
              navigate('/activity');
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-text hover:bg-bg"
          >
            <Activity size={15} className="flex-shrink-0 text-textMute" />
            <span>Atividade</span>
          </button>
          {canUsers && (
            <button
              type="button"
              role="menuitem"
              data-testid="user-menu-users"
              onClick={() => {
                navigate('/users');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-text hover:bg-bg"
            >
              <Users size={15} className="flex-shrink-0 text-textMute" />
              <span>Usuários</span>
            </button>
          )}
          {canClusters && (
            <button
              type="button"
              role="menuitem"
              data-testid="user-menu-clusters"
              onClick={() => {
                navigate('/clusters');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-text hover:bg-bg"
            >
              <Boxes size={15} className="flex-shrink-0 text-textMute" />
              <span>Clusters</span>
            </button>
          )}
          {canOps && (
            <button
              type="button"
              role="menuitem"
              data-testid="user-menu-missions"
              onClick={() => {
                navigate('/ops/missions');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-text hover:bg-bg"
            >
              <Target size={15} className="flex-shrink-0 text-textMute" />
              <span>Missões</span>
            </button>
          )}
          {canOps && (
            <button
              type="button"
              role="menuitem"
              data-testid="user-menu-effectiveness"
              onClick={() => {
                navigate('/ops/effectiveness');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-text hover:bg-bg"
            >
              <Gauge size={15} className="flex-shrink-0 text-textMute" />
              <span>Efetividade</span>
            </button>
          )}
          <div className="border-t border-borderSoft" />
          <button
            type="button"
            role="menuitem"
            data-testid="user-menu-logout"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] text-danger hover:bg-bg"
          >
            <LogOut size={15} className="flex-shrink-0" />
            <span>Sair</span>
          </button>
        </div>
      )}
    </div>
  );
}
